import axios from 'axios';

const BASE_URL = import.meta.env?.VITE_API_BASE_URL || 'https://api.dynamicpaysolutions.com:7000/v1';

const axiosInstance = axios?.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor - attach Bearer token to all requests except auth endpoints
axiosInstance?.interceptors?.request?.use(
  (config) => {
    const skipAuth = ['/auth/login', '/portal/sign-up', '/auth/2fa/setup', '/auth/2fa/verify'];
    const isSkipped = skipAuth?.some(path => config?.url?.includes(path));
    if (!isSkipped) {
      const token = localStorage.getItem('dp_token');
      if (token) {
        config.headers = config?.headers || {};
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export const apiService = {
  token: null,

  setToken(token) {
    this.token = token;
    if (token) {
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axiosInstance?.defaults?.headers?.common?.['Authorization'];
    }
  },

  async get(url, params = {}) {
    return axiosInstance?.get(url, { params });
  },

  async post(url, data, config = {}) {
    return axiosInstance?.post(url, data, config);
  },

  async put(url, data, config = {}) {
    return axiosInstance?.put(url, data, config);
  },

  async delete(url) {
    return axiosInstance?.delete(url);
  },

  // Auth
  login: (data) => axiosInstance?.post('/auth/login', data),
  setup2FA: (data) => axiosInstance?.post('/auth/2fa/setup', data),
  verify2FA: (data) => axiosInstance?.post('/auth/2fa/verify', data),
  resetPassword: (userId, data) => axiosInstance?.put(`/portal/users/${userId}/password/change`, data),

  // Sign Up
  signUp: (data) => axiosInstance?.post('/portal/sign-up', data),

  // Onboarding
  getOnboardingStatus: (registrationId) => axiosInstance?.get(`/portal/onboarding/${registrationId}/users`),
  getOnboardingList: (registrationId) => axiosInstance?.get(`/portal/onboarding/${registrationId}/list`),
  submitBusinessInfo: (registrationId, data) => axiosInstance?.post(`/portal/onboarding/${registrationId}/business`, data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  submitOwners: (registrationId, data) => axiosInstance?.post(`/portal/onboarding/${registrationId}/owners`, data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  submitBankDetails: (registrationId, data) => axiosInstance?.post(`/portal/onboarding/${registrationId}/bank`, data, { headers: { 'Content-Type': 'multipart/form-data' } }),

  // Onboarding Approval (Backoffice)
  listOnboarding: (params) => axiosInstance?.get('/portal/onboarding/list', { params }),
  approveOnboarding: (registrationId) => axiosInstance?.get(`/portal/onboarding/${registrationId}/approve`),
  submitOnboardingDecision: (registrationId, data) => axiosInstance?.post(`/portal/onboarding/${registrationId}/approve`, data),
  listBusinesses: (params) => axiosInstance?.get('/portal/business', { params }),

  // Transactions
  getTransactions: (params) => axiosInstance?.get('/portal/transactions', { params }),

  // Settlements
  getSettlementSummary: ({ business_code, merchants, currencies, start_date, end_date }) => {
    const qs = new URLSearchParams();
    if (business_code) qs?.append('business_code', business_code);
    if (Array.isArray(merchants)) {
      merchants?.forEach(m => qs?.append('merchants', m));
    }
    if (Array.isArray(currencies)) {
      currencies?.forEach(c => qs?.append('currency', c));
    }
    if (start_date) qs?.append('start_date', start_date);
    if (end_date) qs?.append('end_date', end_date);
    return axiosInstance?.get(`/portal/settlements/summary?${qs?.toString()}`);
  },

  getSettlementsList: (params) => axiosInstance?.get('/portal/settlements/list', { params }),
  getSettlementMerchantDetail: (params) => axiosInstance?.get('/portal/settlements/list', { params }),

  // Configurations
  getCurrencies: () => axiosInstance?.get('/portal/configurations/currencies'),

  // API Keys
  getApiKeys: (businessCode) => axiosInstance?.get(`/portal/api-keys`, { params: { business_code: businessCode } }),
  regenerateApiKeys: (merchantId) => axiosInstance?.put(`/portal/api-keys/${merchantId}`),

  // RBAC - Permission Groups
  getPermissionGroups: () => axiosInstance?.get('/portal/rbac/permission-groups'),
  createPermissionGroup: (data) => axiosInstance?.post('/portal/rbac/permission-groups', data),
  updatePermissionGroup: (groupId, data) => axiosInstance?.put(`/portal/rbac/permission-groups/${groupId}`, data),
  disablePermissionGroup: (groupId, data) => axiosInstance?.patch(`/portal/rbac/permission-groups/${groupId}/disable`, data),

  // RBAC - Permissions
  getPermissions: () => axiosInstance?.get('/portal/rbac/permission'),
  createPermission: (data) => axiosInstance?.post('/portal/rbac/permission', data),
  updatePermission: (permissionId, data) => axiosInstance?.put(`/portal/rbac/permission/${permissionId}`, data),
  disablePermission: (permissionId, data) => axiosInstance?.patch(`/portal/rbac/permission/${permissionId}/disable`, data),

  // RBAC - Roles
  getRoles: (businesses) => {
    if (Array.isArray(businesses) && businesses?.length > 0) {
      const qs = new URLSearchParams();
      businesses?.forEach(b => qs?.append('business', b));
      return axiosInstance?.get(`/portal/rbac/roles?${qs?.toString()}`);
    }
    return axiosInstance?.get('/portal/rbac/roles');
  },
  createRole: (data) => axiosInstance?.post('/portal/rbac/roles', data),
  updateRole: (roleId, data) => axiosInstance?.put(`/portal/rbac/roles/${roleId}`, data),
  disableRole: (roleId, data) => axiosInstance?.patch(`/portal/rbac/roles/${roleId}`, data),

  // RBAC - Role-Permission Mapping
  getRolePermissions: (roleId) => axiosInstance?.get(`/portal/rbac/roles/${roleId}/permissions`),
  getAllRolePermissions: (businesses = []) => {
    const qs = new URLSearchParams();
    businesses?.forEach(b => {
      const code = b?.business_code || b;
      if (code) qs?.append('business', code);
    });
    const query = qs?.toString();
    return axiosInstance?.get(query ? `/portal/rbac/roles/permissions?${query}` : '/portal/rbac/roles/permissions');
  },
  assignPermissionsToRole: (roleId, data) => axiosInstance?.post(`/portal/rbac/roles/${roleId}/permissions`, data),

  // RBAC - User-Role Mapping
  getUserRoles: (userId) => axiosInstance?.get(`/portal/rbac/users/${userId}/roles`),
  getAllUserRoles: (businesses = [], userType) => {
    const params = new URLSearchParams();
    if (userType !== undefined && userType !== null && userType !== '') {
      params?.append('user_type', userType);
    }
    businesses?.forEach(b => {
      const code = b?.business_code || b;
      if (code) params?.append('business', code);
    });
    const qs = params?.toString();
    return axiosInstance?.get(`/portal/rbac/users/roles${qs ? `?${qs}` : ''}`);
  },
  assignRolesToUser: (userId, data) => axiosInstance?.post(`/portal/rbac/users/${userId}/roles`, data),

  // Users
  getUsers: (businesses = [], userType) => {
    const params = new URLSearchParams();
    if (userType !== undefined && userType !== null && userType !== '') {
      params?.append('user_type', userType);
    }
    businesses?.forEach(b => {
      const code = b?.business_code || b;
      if (code) params?.append('business', code);
    });
    const query = params?.toString();
    return axiosInstance?.get(query ? `/portal/users?${query}` : '/portal/users');
  },
  getUserTypes: () => axiosInstance?.get('/portal/users/types'),

  // Audit Logs
  getAuditLogs: (params) => axiosInstance?.get('/portal/audit-logs', { params }),

  // Finance - Account Types
  getAccountTypes: () => axiosInstance?.get('/portal/financials/account-types'),

  // Finance - Chart of Accounts
  getChartOfAccounts: () => axiosInstance?.get('/portal/financials/accounts'),
  createChartOfAccount: (data) => axiosInstance?.post('/portal/financials/accounts', data),

  // Payment Links
  createPaymentLink: (data) => axiosInstance?.post('/payment-links', data),
  getPaymentLinks: (params) => axiosInstance?.get('/payment-links', { params }),
  updatePaymentLink: (data) => axiosInstance?.put('/payment-links', data),
};

// Response interceptor for error handling
axiosInstance?.interceptors?.response?.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      const requestUrl = error?.config?.url || '';
      const isLoginRequest = requestUrl?.includes('/auth/login');
      const isOnLoginPage = window.location?.pathname === '/login';
      if (!isLoginRequest && !isOnLoginPage) {
        localStorage.removeItem('dp_token');
        localStorage.removeItem('dp_otp_verified');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
